import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Switch } from 'src/design/atoms/Switch';
import { Text } from 'src/design/atoms/Text';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChatMemoryDto } from 'src/api/FiretrolApiClient';
import { useChatMemoryQuery } from 'src/api/queries/chat/useChatMemoryQueries';
import {
  useAcceptChatMemory,
  useCreateChatMemory,
  useDeclineChatMemory,
  useDeleteAllChatMemory,
  useDeleteChatMemory,
  useSetChatMemoryEnabled,
  useUpdateChatMemory,
} from 'src/api/queries/chat/useChatMemoryMutations';
import {
  isMemoryStale,
  MEMORY_SECTION_ID,
  memoryKindLabel,
  memorySourceLabel,
} from 'src/app/chat/memoryNotes';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { DangerButton } from 'src/design/atoms/DangerButton';
import { DeleteButton } from 'src/design/atoms/DeleteButton';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { Modal } from 'src/design/molecules/Modal';
import { useConfirmableAction } from 'src/design/molecules/useConfirmableAction';
import { MemoryNoteForm } from 'src/routes/AccountProfile/MemoryNoteForm';
import { formatDate } from 'src/utils';

interface IMemoryNoteRowProps {
  note: ChatMemoryDto;
  onEdit: () => void;
  onDelete: () => Promise<unknown>;
}

const MemoryNoteRow: React.FC<IMemoryNoteRowProps> = ({
  note,
  onEdit,
  onDelete,
}) => (
  <Paper p="sm" withBorder data-testid="memory-note">
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Stack gap={4} style={{ minWidth: 0 }}>
        <Group gap="xs">
          <Badge size="sm" variant="light">
            {memoryKindLabel(note.kind)}
          </Badge>
          {isMemoryStale(note) && (
            <Badge size="sm" variant="light" color="yellow">
              Not used in 30 days
            </Badge>
          )}
        </Group>
        <Text c="bright" style={{ overflowWrap: 'anywhere' }}>
          {note.text}
        </Text>
        <Text size="xs" c="bright">
          {memorySourceLabel(note.source)} · Created{' '}
          {formatDate(note.createdAt)} · Last used{' '}
          {note.lastUsedAt ? formatDate(note.lastUsedAt) : 'never'}
        </Text>
      </Stack>
      <Group gap={4} wrap="nowrap">
        <EditActionIcon variant="subtle" label="Edit note" onClick={onEdit} />
        <DeleteActionIcon
          variant="subtle"
          label="Delete note"
          confirmationTitle="Delete this note?"
          confirmationText="F.I.R.E. will stop using this note in your chats."
          onDelete={async () => {
            await onDelete();
          }}
        />
      </Group>
    </Group>
  </Paper>
);

interface IMemorySuggestionRowProps {
  note: ChatMemoryDto;
}

const MemorySuggestionRow: React.FC<IMemorySuggestionRowProps> = ({ note }) => {
  const accept = useAcceptChatMemory();
  const decline = useDeclineChatMemory();
  const pending = accept.isPending || decline.isPending;
  return (
    <Paper p="sm" withBorder data-testid="memory-suggestion">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Group gap="xs">
            <Badge size="sm" variant="light">
              {memoryKindLabel(note.kind)}
            </Badge>
          </Group>
          <Text c="bright" style={{ overflowWrap: 'anywhere' }}>
            {note.text}
          </Text>
          <Text size="xs" c="bright">
            Suggested {formatDate(note.createdAt)}
          </Text>
        </Stack>
        <Group gap="xs" wrap="nowrap">
          <BasicButton
            size="compact-xs"
            loading={accept.isPending}
            disabled={pending}
            onClick={() => accept.mutate(note.id)}
          >
            Save
          </BasicButton>
          <SecondaryButton
            size="compact-xs"
            loading={decline.isPending}
            disabled={pending}
            onClick={() => decline.mutate(note.id)}
          >
            No thanks
          </SecondaryButton>
        </Group>
      </Group>
    </Paper>
  );
};

/** The F.I.R.E. memory section of the profile page; the section id is the target of the chat's Manage memory links. */
export const FireMemorySection: React.FC = () => {
  const location = useLocation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const suggestionsTitleId = useId();
  const [editing, setEditing] = useState<number | 'new' | null>(null);

  const { data, isLoading } = useChatMemoryQuery();
  const setEnabled = useSetChatMemoryEnabled();
  const createNote = useCreateChatMemory();
  const updateNote = useUpdateChatMemory();
  const deleteNote = useDeleteChatMemory();
  const deleteAllNotes = useDeleteAllChatMemory();
  const deleteAll = useConfirmableAction(async () => {
    await deleteAllNotes.mutateAsync();
  }, true);

  const notes = useMemo(
    () => (data?.notes ?? []).filter((note) => note.status === 'active'),
    [data],
  );
  const suggestions = useMemo(
    () => (data?.notes ?? []).filter((note) => note.status === 'proposed'),
    [data],
  );
  const loaded = !!data;
  const enabled = setEnabled.isPending
    ? !!setEnabled.variables
    : data?.enabled ?? true;

  // The section renders below the fold once its notes load, so a link to it
  // can only scroll after that.
  useEffect(() => {
    if (loaded && location.hash === `#${MEMORY_SECTION_ID}`) {
      sectionRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [loaded, location.hash, location.key]);

  return (
    <Paper p="md" mt="md" id={MEMORY_SECTION_ID} ref={sectionRef}>
      <Stack maw="720px">
        <Text fw={600}>F.I.R.E. memory</Text>
        <Text size="sm" c="bright">
          Notes F.I.R.E. keeps about how you like answers. They never give
          access to data, and what you ask in a chat always wins.
        </Text>
        <Switch
          label="Use memory"
          description="When off, F.I.R.E. neither reads nor saves notes in any chat."
          checked={enabled}
          disabled={!loaded}
          onChange={(event) => setEnabled.mutate(event.currentTarget.checked)}
        />
        {isLoading && <Loader size="sm" />}
        {loaded && (
          <>
            {editing === 'new' ? (
              <Paper p="sm" withBorder>
                <MemoryNoteForm
                  onSubmit={async (request) => {
                    await createNote.mutateAsync(request);
                    setEditing(null);
                  }}
                  onCancel={() => setEditing(null)}
                />
              </Paper>
            ) : (
              <Group>
                <CreateButton size="xs" onClick={() => setEditing('new')}>
                  Add a note
                </CreateButton>
                {notes.length + suggestions.length > 0 && (
                  <DangerButton
                    size="xs"
                    onClick={() => deleteAll.requestOrRun()}
                  >
                    Delete all
                  </DangerButton>
                )}
              </Group>
            )}
            {suggestions.length > 0 && (
              <Stack
                gap="xs"
                aria-labelledby={suggestionsTitleId}
                component="section"
              >
                <Text id={suggestionsTitleId} size="sm" fw={600} c="bright">
                  Suggestions
                </Text>
                {suggestions.map((note) => (
                  <MemorySuggestionRow key={note.id} note={note} />
                ))}
              </Stack>
            )}
            {notes.length === 0 && editing !== 'new' && (
              <Text size="sm" c="bright">
                No notes yet. Ask F.I.R.E. to remember something, or add a note
                here.
              </Text>
            )}
            {notes.map((note) =>
              editing === note.id ? (
                <Paper key={note.id} p="sm" withBorder>
                  <MemoryNoteForm
                    initialValues={{ kind: note.kind, text: note.text }}
                    onSubmit={async (request) => {
                      await updateNote.mutateAsync({ id: note.id, request });
                      setEditing(null);
                    }}
                    onCancel={() => setEditing(null)}
                  />
                </Paper>
              ) : (
                <MemoryNoteRow
                  key={note.id}
                  note={note}
                  onEdit={() => setEditing(note.id)}
                  onDelete={() => deleteNote.mutateAsync(note.id)}
                />
              ),
            )}
          </>
        )}
      </Stack>
      <Modal
        size="md"
        title="Delete all memory notes?"
        opened={deleteAll.opened}
        onClose={deleteAll.close}
        actions={[
          <DeleteButton
            key="confirm"
            loading={deleteAll.loading}
            onClick={() => deleteAll.confirm()}
          >
            Delete all
          </DeleteButton>,
        ]}
      >
        <Text>
          F.I.R.E. will forget every note it keeps for you, including
          suggestions. This cannot be undone.
        </Text>
      </Modal>
    </Paper>
  );
};
